/**
 * Riga di una scheda di libreria, uguale nell'elenco (V63) e fra le varianti
 * del dettaglio (V65): il riquadro con l'icona della famiglia prevalente del
 * mix, il nome, RPE e minuti di riferimento e il mix riassunto per famiglia.
 * Il tipo lo dice la card; una scheda la svolge chiunque.
 */

import type { ComponentProps } from 'react';
import { Link } from 'react-router-dom';
import { ClockIcon, GaugeIcon, GitBranchIcon, type LucideIcon } from 'lucide-react';
import {
  ETICHETTA_FAMIGLIA_ATTRIBUTO,
  FAMIGLIE_ATTRIBUTO,
  type FamigliaAttributo,
} from '@calcio/shared';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { IconaFamiglia } from '../comuni/famiglie.js';
import { famigliaPrevalente, type SchedaInElenco, type VarianteInElenco } from './schede.js';

export function RigaScheda({
  scheda: s,
  ss,
}: {
  scheda: SchedaInElenco | VarianteInElenco;
  ss: string;
}) {
  const varianti = 'nVarianti' in s ? s.nVarianti : 0;
  const prevalente = famigliaPrevalente(s.famiglie);
  return (
    <Link
      to={`/sq/${ss}/schede/${s.id}`}
      className="flex items-center gap-3 rounded-lg border border-border bg-card p-2.5"
    >
      <span
        className={cn(
          'flex size-12 shrink-0 items-center justify-center rounded-md border border-border bg-background',
          !s.attiva && 'opacity-60',
        )}
      >
        <RiquadroFamiglia famiglia={prevalente} />
      </span>

      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex flex-wrap items-center gap-2">
          <span
            className={cn(
              'text-base font-semibold break-words',
              !s.attiva && 'text-muted-foreground',
            )}
          >
            {s.nome}
          </span>
          {!s.attiva && <Badge variant="outline">Disattivata</Badge>}
        </span>
        <span className="flex flex-col gap-1 text-sm @md:flex-row @md:items-center @md:justify-between @md:gap-3">
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <DatiScheda rpe={s.rpeRiferimento} minuti={s.minutiRiferimento} varianti={varianti} />
          </span>
          <MixPerFamiglia famiglie={s.famiglie} />
        </span>
      </span>
    </Link>
  );
}

/** Il contenuto del riquadro a sinistra della riga: l'icona della famiglia prevalente. */
export function RiquadroFamiglia({ famiglia }: { famiglia: FamigliaAttributo | null }) {
  return famiglia && <IconaFamiglia famiglia={famiglia} className="size-5" />;
}

/** RPE, minuti e varianti con le loro icone: nella riga e sotto il titolo del dettaglio. */
export function DatiScheda({
  rpe,
  minuti,
  varianti = 0,
}: {
  rpe: number;
  minuti: number;
  varianti?: number;
}) {
  const voce = (Icona: LucideIcon, testo: string, titolo: string) => (
    <span className="flex items-center gap-1 text-muted-foreground" title={titolo}>
      <Icona aria-hidden className="size-4" />
      <span className="font-medium text-foreground tabular-nums">{testo}</span>
    </span>
  );
  return (
    <>
      {voce(GaugeIcon, `RPE ${rpe}`, 'RPE di riferimento')}
      {voce(ClockIcon, `${minuti}′`, 'Minuti di riferimento')}
      {varianti > 0 &&
        voce(
          GitBranchIcon,
          varianti === 1 ? '1 variante' : `${varianti} varianti`,
          'Varianti della scheda',
        )}
    </>
  );
}

/** Il mix sommato per famiglia: l'icona della famiglia e la sua quota. */
export function MixPerFamiglia({
  famiglie,
  className,
  ...props
}: { famiglie: SchedaInElenco['famiglie'] } & ComponentProps<'span'>) {
  const presenti = FAMIGLIE_ATTRIBUTO.filter((f) => famiglie[f]);
  if (presenti.length === 0) return null;
  return (
    <span className={cn('flex items-center gap-3', className)} {...props}>
      {presenti.map((f) => (
        <span
          key={f}
          className="flex items-center gap-1 text-muted-foreground"
          title={`${ETICHETTA_FAMIGLIA_ATTRIBUTO[f]} ${famiglie[f]}%`}
        >
          <IconaFamiglia famiglia={f} className="size-4" />
          <span className="font-medium text-foreground tabular-nums">{famiglie[f]}</span>
        </span>
      ))}
    </span>
  );
}
